// @flow strict

import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { NewsletterIcon } from '~components/icons/newsletter-icon';
import { SocialMediaIcon } from '~components/icons/social-media/social-media-icon';

import type { Post } from '~types/post';

import * as styles from './sidebar.module.less';

/**
 * Type of component properties
 */
type SidebarProps = {
  post?: Post,
};

/**
 * Display sidebar, with newsletter button & social media buttons.
 *
 * @param {SidebarProps} props - component properties
 *
 * @returns {Node}
 */
export const Sidebar = ({ post }: SidebarProps) => {
  // get static page data
  const data = useStaticQuery(graphql`
    query siteData {
      site {
        siteMetadata {
          domain
          twitter {
            account
          }
        }
      }
    }
  `);

  const siteData = data.site.siteMetadata;
  const { account: twitterAccount } = siteData.twitter;

  let xingLink, linkedinLink, twitterLink, facebookLink;

  // if blog post is available, generate share links for social media
  if (post != null) {
    const { permalink, title } = post;

    const domain = siteData.domain;
    const link = encodeURI(`${domain}${permalink}`);
    const encodedTitle = encodeURIComponent(title);

    xingLink = `https://www.xing.com/spi/shares/new?url=${link}`;
    linkedinLink = `https://www.linkedin.com/shareArticle?mini=true&url=${link}&title=${encodedTitle}`;
    twitterLink = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${link}&via=${twitterAccount}`;
    facebookLink = `https://www.facebook.com/sharer/sharer.php?u=${link}`;
  } else {
    // otherwise use default account urls
    xingLink = `https://www.xing.com/companies/omqgmbh`;
    linkedinLink = `https://www.linkedin.com/company/omq`;
    twitterLink = `https://twitter.com/OMQ_AI`;
    facebookLink = `https://www.facebook.com/OMQ-741045522692005`;
  }

  return (
    <aside className={styles.sidebar}>
      {/* Newsletter section */}
      <div className={styles.newsletter}>
        <a href="#newsletter-signup">
          <figure>
            <NewsletterIcon className={styles.image} />
          </figure>
        </a>
      </div>

      {/* Social media section */}
      <div className={styles.socialMediaList}>
        <a href={xingLink} target="_blank" rel="noopener noreferrer">
          <SocialMediaIcon
            type="xing"
            color="gray"
            svgProps={{ className: styles.socialMediaIcon }}
          />
        </a>

        <a href={linkedinLink} target="_blank" rel="noopener noreferrer">
          <SocialMediaIcon
            type="linkedin"
            color="gray"
            svgProps={{ className: styles.socialMediaIcon }}
          />
        </a>

        <a href={twitterLink} target="_blank" rel="noopener noreferrer">
          <SocialMediaIcon
            type="twitter"
            color="gray"
            svgProps={{ className: styles.socialMediaIcon }}
          />
        </a>

        <a href={facebookLink} target="_blank" rel="noopener noreferrer">
          <SocialMediaIcon
            type="facebook"
            color="gray"
            svgProps={{ className: styles.socialMediaIcon }}
          />
        </a>
      </div>
    </aside>
  );
};
